import { NoPermissionState } from "@/components/common/no-permission-state";
import { isForbiddenError } from "@/lib/clinic-grants";
import { Skeleton } from "@/components/ui/skeleton";
import { useFetchFinanceEntriesByIdAccountQuery, usePermission } from "@/hooks";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  FileText,
  ListFilter,
  PiggyBank,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { formatValue } from "@/utils";
import {
  DataTableEntries,
  FinanceCard,
  FormFilterFinanceEntries,
  FormFinanceEntry,
} from "@/components/common";

export function FinanceEntries() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const { id } = useParams<{ id: string }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page");

  const perPageParam = searchParams.get("per_page");
  const [perPage, setPerPage] = useState<number>(
    perPageParam ? Number(perPageParam) : 10
  );

  const categoryId = searchParams.get("categoryId");
  const patientId = searchParams.get("patientId");
  const procedureId = searchParams.get("procedureId");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const type = searchParams.get("type");

  const { data, isLoading, error: listError } = useFetchFinanceEntriesByIdAccountQuery({
    accountId: id || "",
    page: page || "1",
    per_page: perPage,
    categoryId: categoryId || "",
    patientId: patientId || "",
    procedureId: procedureId || "",
    startDate: startDate || "",
    endDate: endDate || "",
    type: type || "",
  });

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("per_page", newPerPage.toString());
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    if (!checkPermission("finance entry", "view finance entry")) {
      navigate("/")
      return
    }
  }, [])

  // SEC-GATES-02: a refusal from the list endpoint is stated, not shown as an empty list.
  if (isForbiddenError(listError)) return <NoPermissionState />;

  return (
    <main className="mt-5 w-full">
      {isLoading ? (
        <Skeleton className="h-20 w-full max-w-lg" />
      ) : (
        <PageHeader
          eyebrow={
            <span className="inline-flex items-center gap-2">
              Financeiro · {data && t(data.account.name)}
              <Badge className="border-transparent bg-app-surface-muted tracking-normal text-app-fg hover:bg-app-border-subtle">
                {data && data.account.shared > 0
                  ? t("LABEL.public")
                  : t("LABEL.private")}
              </Badge>
            </span>
          }
          title="Lançamentos"
          lede={
            data
              ? `Saldo realizado de ${formatValue(data.realized)} · previsto ${formatValue(data.projected)}.`
              : undefined
          }
          actions={
            <>
              <FormFilterFinanceEntries>
                <Button variant="secondary" className="gap-3">
                  <ListFilter size={16} /> {t("LABEL.filter")}
                </Button>
              </FormFilterFinanceEntries>
              <Button variant="secondary" className="gap-3" asChild>
                <Link to={`/reports/entries?accountId=${id}`}>
                  <FileText size={16} /> {t("LABEL.generate report")}
                </Link>
              </Button>
              <FormFinanceEntry>
                <Button
                  variant="petrol"
                  disabled={
                    !checkPermission("finance entry", "create finance entry")
                  }
                >
                  {t("LABEL.new entry")}
                </Button>
              </FormFinanceEntry>
            </>
          }
        />
      )}

      <section className="mt-16 mb-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        {isLoading && (
          <>
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton className="h-52" key={index} />
            ))}
          </>
        )}
        {!isLoading && data && (
          <>
            <FinanceCard
              icon={ArrowUpCircle}
              iconBgColor="bg-app-info/10"
              iconTextColor="text-app-info"
              label={t("LABEL.revenue")}
              realizedValue={data.realized_income}
              projectedValue={data.projected_income}
            />
            <FinanceCard
              icon={ArrowDownCircle}
              iconBgColor="bg-app-danger/10"
              iconTextColor="text-app-danger"
              label={t("LABEL.expense")}
              realizedValue={data.realized_expense}
              projectedValue={data.projected_expense}
            />
            <FinanceCard
              icon={PiggyBank}
              iconBgColor="bg-app-success/10"
              iconTextColor="text-app-success"
              label={t("LABEL.balance")}
              realizedValue={data.realized}
              projectedValue={data.projected}
            />
          </>
        )}
      </section>

      <p className="mb-4 text-sm text-app-fg-muted">
        Totais de todas as páginas, conforme os filtros. Sem período informado, usamos o mês atual.
        Realizado considera a data do pagamento; em aberto e tabela, o vencimento.
      </p>
      <section>
        {isLoading ? (
          <Skeleton className="w-full h-96" />
        ) : (
          <>
            {data && (
              <DataTableEntries
                handlePerPageChange={handlePerPageChange}
                per_page={perPage}
                data={data}
              />
            )}
          </>
        )}
      </section>
    </main>
  );
}
